import { getCloudProductListsAPI, getPlanById } from '@/api/cart';
import {
    ICountryGroup,
    IDeviceCategory,
    IDeviceModel,
    IDeviceModelGroup,
    IGetCloudProductsArgs,
    IGetCloudProductsResponse,
    IPlan,
    IPlanSeries,
    IAiGroup,
    IStorageRegion,
    ESeriesCategory,
    IAlarmType,
    EPlanStatus,
    ICountry
} from '@reolink-cloud/interface';
import { config } from 'CONFIG';
import * as _ from '@reolink-web/utils';

export interface IGetPlanSeriesMapArgs {
    /** 套餐类型 */
    category: ESeriesCategory;
    /** 对应国家 */
    country: string;
    /** 默认选中的套餐(只有在切换套餐的时候才会传入) */
    defaultSelectedPlan?: string;
    /** 是否去掉免费套餐 */
    dropFreePlan?: boolean;

    dropPlanIds?: string[];

    onlyKeepPlanCanSwitch?: boolean;
}

class ProductsManager {

    public initial: boolean = false;

    public country: IGetCloudProductsResponse['country'] = 'US';

    private _lang: string = 'en';

    private planSeries: IGetCloudProductsResponse['planSeries'] = {} ;

    private plans: IGetCloudProductsResponse['plans'] = {};

    private storageRegions: IGetCloudProductsResponse['storageRegions'] = {};

    private countryGroups: IGetCloudProductsResponse['countryGroups'] = {};

    private deviceModelGroups: IGetCloudProductsResponse['deviceModelGroups'] = {};

    private deviceModels: IGetCloudProductsResponse['deviceModels'] = {};

    private deviceCategory: IGetCloudProductsResponse['deviceCategory'] = [];

    private country2Region: IGetCloudProductsResponse['country2Region'] = {};

    private aiGroup: IGetCloudProductsResponse['aiGroup'] = {};

    private aiType: IGetCloudProductsResponse['aiType'] = {};

    private availableCountryList: Array<ICountry['key']> = [];

    private cloudModels: string[] = [];

    private readonly blockList: string[] = [];

    public get countryList(): ICountry[] {

        return _.Array.unique( Object.values( this.countryGroups ).reduce<ICountry[]>(( acc, group ) => {

            return acc.concat( group.countries );

        }, [] ), country => country.key );

    }

    /**
     * 初始化/更新 数据
     * @param dropLegacy 是否删除legacy套餐
     */
    public async update( args: IGetCloudProductsArgs, dropLegacy: boolean = false ): Promise<void> {

        this._lang = args.lang ?? 'en';

        let data = await getCloudProductListsAPI( args );

        // 获取机型的详细信息，包括图片，链接，名称
        Object.keys( data.deviceModelGroups ).map( group => {

            const models = data.deviceModelGroups[group].models;
            const modelArr: IDeviceModelGroup['modelsDetails'] = [];
            for ( const model of models ) {

                if ( !modelArr.find( item => item.key === model )) {

                    modelArr.push({
                        image: `${data.deviceModels[model].styles.imageUrl ?? ''}`,
                        link: `${data.deviceModels[model].styles.link ?? config.reolink}`,
                        text: data.deviceModels[model]?.title ?? '',
                        visible: data.deviceModels[model]?.visible ?? 'disabled',
                        key: model
                    });

                }

            }

            data.deviceModelGroups[group].modelsDetails = modelArr;

        });
        /** 若后端返回系列与套餐都为空，请求最新的套餐 */
        if ( args.legacyTag && !data.plans && !data.planSeries ) {

            data = await getCloudProductListsAPI({ ...args, legacyTag: '' });

        }

        const {
            planSeries,
            plans,
            storageRegions,
            countryGroups,
            deviceCategory,
            deviceModelGroups,
            deviceModels,
            country2Region,
            aiGroup,
            aiType,
            country
        } = data;
        for ( const plan of Object.values( plans )) {

            plan.series = planSeries[plan.seriesKey];
            plan.title = plan.series.i18n.name;

        }

        this.planSeries = planSeries;
        this.plans = plans;
        this.storageRegions = storageRegions;
        this.countryGroups = countryGroups;
        this.deviceCategory = deviceCategory;
        this.deviceModelGroups = deviceModelGroups;
        this.deviceModels = deviceModels;
        this.aiGroup = aiGroup;
        this.aiType = aiType;
        this.country = country;
        this.country2Region = country2Region;

        if ( dropLegacy ) {

            Object.keys( this.planSeries ).map( key => {

                if ( this.planSeries[key].legacyTag ) {

                    delete this.planSeries[key];

                }

            });

        }

        this.generateAvailableCountries();

        this.generateCloudModels();

        this.initial = true;

    }

    public getPlans(): Record<string, IPlan> {

        return this.plans;

    }

    public getPlanSeries(): Record<string, IPlanSeries> {

        return this.planSeries;

    }

    public getPlanSeriesByKey( key: string ): IPlanSeries {

        return this.planSeries[key] ?? {};

    }

    public async getPlanById( id: string ): Promise<IPlan> {

        if ( !this.plans[id] ) {

            const { plan, series } = await getPlanById( id, this._lang );
            plan.series = series;

            this.plans[id] = plan;
            if ( !this.planSeries[series.key] ) {

                this.planSeries[series.key] = series;

            }

        }
        return this.plans[id];

    }

    public getStorageRegionByKey( key: string ): IStorageRegion {

        return this.storageRegions[key];

    }

    public getCountryGroupByKey( key: string ): ICountryGroup {

        return this.countryGroups[key];

    }

    public getDeviceModelGroupByKey( key: string ): IDeviceModelGroup {

        return this.deviceModelGroups[key];

    }

    public getDeviceModelByKey( key: string ): IDeviceModel {

        return this.deviceModels[key] ?? [];

    }

    public getDeviceCategoryByKey( key: string ): IDeviceCategory {

        return this.deviceCategory[key] ?? [];

    }

    public getAiGroupById( id: string ): IAiGroup {

        return this.aiGroup[id];

    }

    public getTotalAlarmTypes(): IAlarmType[] {

        return Object.keys( this.aiType ).map( key => this.aiType[key] );

    }

    public getAlarmTypeInfoByKey( key: string ): IAlarmType {

        return this.aiType[key];

    }

    /**
     * 排序规则：先排父类型，然后将排序后的子类型放到父类型后面
     * @returns {IAlarmType[]} 排序后的告警类型
     */
    public getSortedTotalAlarmTypes(): IAlarmType[] {

        const sortedTotalAlarmTypes: IAlarmType[] = [];
        const totalAlarmTypes = this.getTotalAlarmTypes();
        const sortedTotalParent = totalAlarmTypes.filter( item => item.parentId === 0 ).sort(( a, b ) => a.meta!.sort - b.meta!.sort );
        for ( const parent of sortedTotalParent ) {

            sortedTotalAlarmTypes.push( parent );
            const children = totalAlarmTypes.filter( child => child.parentId === parent.id ).sort(( a, b ) => a.meta!.sort - b.meta!.sort  );
            sortedTotalAlarmTypes.push( ...children );

        }

        return sortedTotalAlarmTypes;

    }

    // public getAlarmTypesByGroupId( groupId: string ): IAlarmType[]{

    // }

    public getRegionByCountry( country: string ): string {

        return this.country2Region[country] ?? '';

    }

    public getAvailableCountryList(): string[] {

        return this.availableCountryList;

    }

    public getDefaultCountryKey( userCountry?: string ): string | undefined {

        const countryKey = this.availableCountryList.find( country => {

            if ( userCountry ) {

                return country === userCountry;

            } else {

                return country === this.country;

            }

        });
        return countryKey;

    }

    public getPlanSeriesListByCategory( category: ESeriesCategory ): IPlanSeries[] {

        const result: IPlanSeries[] = [];

        for ( const seriesKey of Object.keys( this.planSeries )) {

            if ( this.planSeries[seriesKey].category === category ) {

                result.push( this.planSeries[seriesKey] );

            }

        }

        return result;

    }

    /**
     * 通过系列key筛选plans
     * @param seriesKey 系列key
     * @returns
     */
    public getPlansBySeriesKey( seriesKey: string ): IPlan[] {

        const result: IPlan[] = [];

        for ( const plan of Object.values( this.plans )) {

            if ( plan.seriesKey === seriesKey ) {

                result.push( plan );

            }

        }

        return result;

    }

    /**
     * 获取对应类型的套餐
     * @param {IGetPlanSeriesMapArgs} args
     * @returns {IPlanByStrategyAndCycle} 系列表，键为系列key，值为策略表
     */
    public getPlanSeriesMap( args: IGetPlanSeriesMapArgs ): IPlanByStrategyAndCycle {

        const result: IPlanByStrategyAndCycle = {};

        for ( const seriesKey of Object.keys( this.planSeries )) {

            const seriesCountryGroup = this.planSeries[seriesKey].countryGroups;

            if ( this.planSeries[seriesKey].category === args.category && this.isCurrentCountry( args.country, seriesCountryGroup )) {

                const plans = this.getPlansBySeriesKey( seriesKey );
                for ( const plan of plans ) {

                    if ( args.dropFreePlan && plan.pricing.salePrice === '0.00' ) {

                        continue;

                    }
                    if ( args.dropPlanIds?.includes( plan.id )) {

                        continue;
                    
                    }
                    if ( args.onlyKeepPlanCanSwitch && !plan.limitations.switchFromOther ) {

                        continue;
                    
                    }
                    const cycle = plan.duration;
                    const storageStrategy = plan.storageStrategy;
                    result[seriesKey] = result[seriesKey] ?? {};
                    result[seriesKey][storageStrategy] = result[seriesKey][storageStrategy] ?? {};
                    result[seriesKey][storageStrategy][`${cycle.amount}-${cycle.unit}`] = result[seriesKey][storageStrategy][`${cycle.amount}-${cycle.unit}`] ?? [] ;

                    const checked = plan.id === args.defaultSelectedPlan && plan.limitations.switchFromOther && Number( plan.pricing.salePrice ) !== 0 && plan.status === EPlanStatus.ENABLED;
                    result[seriesKey][storageStrategy][`${cycle.amount}-${cycle.unit}`].push({ ...plan, checked: checked });
                    result[seriesKey][storageStrategy][`${cycle.amount}-${cycle.unit}`].sort(( planA, planB ) => planB['style']['plan.sort'] - planA['style']['plan.sort'] );

                }

            }

        }
        return result;

    }

    public addPlan( plan: IPlan ): void {

        if ( !this.plans[plan.id] )
            this.plans[plan.id] = plan;

    }

    public getBelongDeviceGroupsKeyByModelTitle( modelTitle: string ): string[] {

        const result: string[] = [];
        for ( const [key, value] of Object.entries( this.deviceModelGroups )) {

            if ( value.modelsDetails.find(( item ) => item.text === modelTitle )) {

                result.push( key );

            }

        }

        return result;

    }

    /** 根据系列数据，构造可用国家组列表 */
    private generateAvailableCountryGroupList(): Array<keyof IGetCloudProductsResponse['countryGroups']> {

        let countryGroups: string[] = [];
        for ( const planSeries of Object.values( this.planSeries )) {

            if ( [ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN, ESeriesCategory.CLOUD_DATA_PLAN].includes( planSeries.category )) {

                countryGroups = countryGroups.concat( planSeries.countryGroups );

            }

        }
        return [...new Set( countryGroups )];

    }

    /** 根据可用国家组列表，构造可用国家列表 */
    private generateAvailableCountries(): void {

        const availableCountryGroupKeys = this.generateAvailableCountryGroupList();

        const countryList: string[] = [];
        for ( const countryGroupKey of availableCountryGroupKeys ) {

            for ( const country of this.countryGroups[countryGroupKey].countries ) {

                countryList.push( country.key );

            }

        }
        this.availableCountryList = [...new Set( countryList )];

    }

    /** 判定当前国家是否在指定的国家组中 */
    private isCurrentCountry( country: string, countryGroupKeys: string[] ): boolean {

        for ( const countryGroupKey of countryGroupKeys ) {

            const countryGroupData = this.getCountryGroupByKey( countryGroupKey );

            const countries = countryGroupData.countries.map( item => item.key );

            if ( countries.includes( country )) {

                return true;

            }

        }
        return false;

    }

    private generateCloudModels(): void {

        let cloudModels: string[] = [];
        for ( const modelGroup of Object.values( this.deviceModelGroups )) {

            const models = modelGroup.modelsDetails.map( item => item.text );
            cloudModels = [...cloudModels, ...models];

        }
        this.cloudModels = Array.from( new Set( cloudModels ));

    }

    public getCloudModels(): string[] {

        return this.cloudModels;

    }

    public getPlansByStorageRegion( country: string ): IPlan[] {

        const result: IPlan[] = [];
        const storageRegion = this.storageRegions[this.country2Region[country]];

        for ( const plan of Object.values( this.plans )) {

            if ( plan.series?.storageRegion === storageRegion?.key ) {

                result.push( plan );

            }

        }

        return result;

    }

    public getCountryText( key: string ): string {

        return this.countryList.find( item => item.key === key )?.displayName ?? key;

    }

    public setBlockList( blockList: string[] ): void {

        this.blockList.push( ...blockList );
        for ( const key in this.plans ) {

            if ( this.blockList.includes( key )) {

                delete this.plans[key];

            }

        }

    }

}

/**
 * 产品管理器
 * - 建议每次使用时update
 */
export const productsManager = new ProductsManager();

/**
 * 周期表，键为周期，值为plan套餐数组
 */
export type ICycleMap = Record</** 周期 */string, Array<IPlan & { checked: boolean; }>>;
/**
 * 策略表，键为策略key，值为周期表
 */
export type IStrategyMap = Record</** 策略key */string, ICycleMap>;
/**
 * 系列表，键为系列key，值为策略表
 */
export type IPlanByStrategyAndCycle = Record</** 系列key */string, IStrategyMap> & { checked?: boolean; };
